import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Actualizar el estado para que la próxima renderización muestre la UI de error
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Registrar el error en un servicio de logging (puede ser integrado más adelante)
    console.error('Error capturado por ErrorBoundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // UI de error personalizada
      return <ErrorFallback onReset={this.handleReset} error={this.state.error} navigate={this.props.navigate} />;
    }

    return this.props.children;
  }
}

// Componente de fallback para mostrar cuando hay un error
const ErrorFallback = ({ onReset, error, navigate }) => {

  const handleGoHome = () => {
    onReset();
    if (navigate) {
      navigate('/dashboard');
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3
        }}
      >
        <Box sx={{ mb: 3 }}>
          <ErrorOutlineIcon
            sx={{
              fontSize: 80,
              color: 'error.main',
              mb: 2
            }}
          />
        </Box>

        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
          ¡Oops! Algo salió mal
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          Lo sentimos, ha ocurrido un error inesperado en la aplicación.
          Por favor, intenta recargar la página o volver al inicio.
        </Typography>

        {process.env.NODE_ENV === 'development' && error && (
          <Box
            sx={{
              mb: 4,
              p: 2,
              bgcolor: 'error.light',
              borderRadius: 2,
              textAlign: 'left',
              maxHeight: 200,
              overflow: 'auto'
            }}
          >
            <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {error.toString()}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            size="large"
          >
            Volver al Inicio
          </Button>
          <Button
            variant="outlined"
            onClick={handleReload}
            size="large"
          >
            Recargar Página
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ErrorBoundary;
